import { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logoBlack from "/src/assets/logo-black.png";
import logoWhite from "/src/assets/logo-white.png";
import { LayoutContext } from "../context/LayoutContext";
import { MachineContext } from "../context/MachineContext";
import { SiteContentContext } from "../context/SiteContentContext.jsx";
import { UserContext } from "../context/UserContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { useScrollLock } from "../hooks/useScrollLock";
import { categoryUrlSlug } from "../slug.js";
import ContentText from "./contentText.jsx";
import LoginOrRegisterModal from "./loginOrRegisterModal.jsx";
import SearchModal from "./searchModal.jsx";

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <title>Sök</title>
    <circle cx="11" cy="11" r="8" />
    <path strokeLinecap="round" d="m21 21-4.35-4.35" />
  </svg>
);

const GlobeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <title>Språk</title>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <title>Användare</title>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0zM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632z"
    />
  </svg>
);

const ChevronDownIcon = ({ className = "" }) => (
  <svg
    className={`w-3.5 h-3.5 ${className}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <title>Expand</title>
    <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
  </svg>
);

function DesktopDropdown({ label, url, children, textCls, onLabelClick }) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  function handleMouseEnter() {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  }

  function handleMouseLeave() {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  }

  return (
    <nav className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Link
        to={url}
        onClick={(e) => {
          if (onLabelClick) {
            e.preventDefault();
            onLabelClick();
          }
        }}
        className={`flex items-center gap-1 text-base font-medium transition-colors px-3 py-2 ${textCls}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {label}
        <ChevronDownIcon className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </Link>

      {isOpen && (
        <div className="absolute top-full left-0 mt-0 w-64 bg-surface rounded-md shadow-lg border border-border-subtle py-2 z-50 max-h-[70vh] overflow-y-auto">
          {children}
        </div>
      )}
    </nav>
  );
}

function MobileAccordion({ label, url, onNavigate, onLabelClick, children }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border-b border-gray-200">
      <div className="flex items-center">
        <Link
          to={url}
          className="flex-1 px-4 py-3 text-gray-900 text-base font-medium"
          onClick={(e) => {
            if (onLabelClick) {
              e.preventDefault();
              onLabelClick();
            } else {
              onNavigate?.();
            }
          }}
        >
          {label}
        </Link>
        <button
          type="button"
          className="px-4 py-3 text-gray-900"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? "Stäng" : "Öppna"} ${label} undermeny`}
        >
          <ChevronDownIcon className={`transition-transform ${isExpanded ? "rotate-180" : ""}`} />
        </button>
      </div>
      {isExpanded && <div className="bg-gray-50 pb-2">{children}</div>}
    </div>
  );
}

function NavLink({ url, label, className }) {
  return (
    <Link to={url} className={className}>
      {label}
    </Link>
  );
}

const DEFAULT_LANGUAGES = [
  { code: "sv", label: "Svenska" },
  { code: "en", label: "English" },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loginModalOpened, setLoginModalOpened] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const langDropdownRef = useRef(null);
  const { roles, userEmail, logout, onLogin } = useContext(UserContext);
  // Any role opens the admin area; its landing page shows each role only its own pages.
  const showAdminLink = roles.length > 0;
  const { machineCategories } = useContext(MachineContext);
  const { getContent, languages } = useContext(SiteContentContext);
  const { transparentHeader } = useContext(LayoutContext);
  const { locale, localePath } = useLocalePath();
  const location = useLocation();
  const navigate = useNavigate();

  const isTransparent = transparentHeader && !scrolled;

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 10);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const configuredLanguages = languages.length > 0 ? languages : DEFAULT_LANGUAGES;

  const categoryItems = machineCategories
    .filter((c) => c.amountOfRelatedMachines > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // biome-ignore lint/correctness/useExhaustiveDependencies: location.pathname intentionally triggers close
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useScrollLock(mobileMenuOpen);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileMenuOpen]);

  const closeMobile = () => setMobileMenuOpen(false);

  // "Produkter" scrolls to the homepage product-catalog section instead of opening the
  // catalogue page. If we're not on the homepage, navigate there first and let HomePage
  // pick up the scroll target once the section has mounted.
  const goToProducts = () => {
    setMobileMenuOpen(false);
    const homePath = localePath("/");
    const onHome = location.pathname.replace(/\/+$/, "") === homePath.replace(/\/+$/, "");
    if (onHome) {
      document.getElementById("product-catalog")?.scrollIntoView({ behavior: "smooth" });
    } else {
      // Navigate home with a hash; HomePage scrolls to the section once it mounts.
      navigate(`${homePath}#product-catalog`);
    }
  };

  // Ctrl+K / Cmd+K to open search
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Close language dropdown on outside click
  useEffect(() => {
    if (!langDropdownOpen) return;
    function handleClick(e) {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [langDropdownOpen]);

  function switchToLocale(newLocale) {
    const path = location.pathname.replace(/^\/(sv|en)/, `/${newLocale}`);
    navigate(path + location.search + location.hash);
    setLangDropdownOpen(false);
  }

  const directNavItems = [
    { label: getContent("nav.about_us", "Om oss"), url: localePath("/about-us") },
    { label: getContent("nav.news", "Nyheter"), url: localePath("/news") },
    { label: getContent("nav.jobs", "Karriär"), url: localePath("/jobs") },
    { label: getContent("nav.contact", "Kontakt"), url: localePath("/contact") },
  ];

  const navLinkCls = isTransparent
    ? "text-white/90 hover:text-white"
    : "text-gray-900 hover:text-gray-600";
  const utilBtnCls = isTransparent
    ? "text-white/80 hover:text-white"
    : "text-gray-600 hover:text-gray-900";

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,border-color,box-shadow] duration-300 ${
          isTransparent
            ? "bg-transparent border-b border-transparent"
            : "bg-white border-b border-border-subtle shadow-sm"
        }`}
      >
        <nav className="container mx-auto max-w-7xl px-4 sm:px-8 h-16 grid grid-cols-[auto_1fr_auto] items-center">
          {/* Logo */}
          <Link to={localePath("/")} className="flex-shrink-0" data-testid="as-maskiner-logo-link">
            <img
              src={isTransparent ? logoWhite : logoBlack}
              alt="AS Maskiner"
              className="h-12 lg:h-14"
            />
          </Link>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center justify-center gap-1">
            {/* Produkter dropdown with dynamic categories */}
            <DesktopDropdown
              label={getContent("nav.products", "Produkter")}
              url={localePath("/catalogue")}
              textCls={navLinkCls}
              onLabelClick={goToProducts}
            >
              <Link
                to={localePath("/catalogue")}
                className="block px-4 py-2 text-sm font-medium text-primary hover:bg-surface-soft transition-colors"
              >
                <ContentText contentKey="nav.products_all" fallback="Alla produkter" />
              </Link>
              <div className="border-t border-border-subtle my-1" />
              {categoryItems.map((category) => (
                <Link
                  key={category.slug}
                  to={localePath(`/catalogue/${categoryUrlSlug(category)}`)}
                  className="flex items-center justify-between px-4 py-2 text-sm text-text-body hover:bg-surface-soft hover:text-primary transition-colors"
                >
                  <span>{category.name}</span>
                  <span className="text-xs text-text-muted">
                    {category.amountOfRelatedMachines}
                  </span>
                </Link>
              ))}
            </DesktopDropdown>

            {/* Direct links */}
            {directNavItems.map((item) => (
              <NavLink
                key={item.label}
                url={item.url}
                label={item.label}
                className={`text-base font-medium transition-colors px-3 py-2 ${navLinkCls}`}
              />
            ))}
          </div>

          {/* Desktop utility links */}
          <div className="hidden lg:flex items-center justify-end gap-4">
            <div className="relative" ref={langDropdownRef}>
              <button
                type="button"
                className={`flex items-center gap-1 text-sm transition-colors ${utilBtnCls}`}
                onClick={() => setLangDropdownOpen((prev) => !prev)}
                aria-label="Byt språk"
                aria-expanded={langDropdownOpen}
                aria-haspopup="true"
              >
                <GlobeIcon /> <span className="uppercase">{locale}</span>
                <ChevronDownIcon
                  className={`transition-transform ${langDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              {langDropdownOpen && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-surface rounded-md shadow-lg border border-border-subtle py-1 z-50">
                  {configuredLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                        lang.code === locale
                          ? "bg-primary/5 text-primary font-medium"
                          : "text-text-body hover:bg-surface-soft hover:text-primary"
                      }`}
                      onClick={() => switchToLocale(lang.code)}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {userEmail ? (
              <div className="flex items-center gap-2">
                {showAdminLink && (
                  <Link to="/admin" className={`text-sm transition-colors ${utilBtnCls}`}>
                    Admin
                  </Link>
                )}
                <button
                  type="button"
                  className={`flex items-center gap-1 text-sm transition-colors ${utilBtnCls}`}
                  onClick={logout}
                >
                  <UserIcon />{" "}
                  <span>
                    <ContentText contentKey="header.logout" fallback="Logga ut" />
                  </span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={`flex items-center gap-1 text-sm transition-colors ${utilBtnCls}`}
                onClick={() => setLoginModalOpened(true)}
              >
                <UserIcon />
              </button>
            )}

            <button
              type="button"
              className={`p-2 transition-colors ${utilBtnCls}`}
              aria-label="Sök"
              onClick={() => setSearchOpen(true)}
            >
              <SearchIcon />
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            className={`lg:hidden ml-auto p-2 transition-colors ${isTransparent ? "text-white" : "text-gray-900"}`}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation-drawer"
            aria-label={mobileMenuOpen ? "Stäng meny" : "Öppna meny"}
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <title>Stäng meny</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <title>Öppna meny</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </nav>
      </header>

      {/* Mobile navigation drawer */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Mobilmeny"
        >
          <button
            type="button"
            aria-label="Stäng meny"
            className="absolute inset-0 bg-overlay"
            onClick={closeMobile}
          />
          <div
            id="mobile-navigation-drawer"
            className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-xl overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <span className="text-gray-500 text-xs tracking-wider uppercase">Meny</span>
              <button
                type="button"
                aria-label="Stäng meny"
                className="text-gray-900"
                onClick={closeMobile}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <title>Stäng meny</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div>
              {userEmail ? (
                <div className="flex flex-col px-4 py-3 border-b border-gray-200">
                  <span className="text-gray-600 text-sm break-words">{userEmail}</span>
                  {showAdminLink && (
                    <Link to="/admin" className="text-xs text-gray-900 mt-1" onClick={closeMobile}>
                      Admin
                    </Link>
                  )}
                  <button
                    type="button"
                    className="text-xs cursor-pointer text-gray-900 text-left mt-1"
                    onClick={() => {
                      closeMobile();
                      logout();
                    }}
                  >
                    <ContentText contentKey="header.logout" fallback="Logga ut" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="block w-full text-left px-4 py-3 text-gray-700 border-b border-gray-200"
                  onClick={() => {
                    closeMobile();
                    setLoginModalOpened(true);
                  }}
                >
                  <ContentText contentKey="header.login" fallback="LOGGA IN" />
                </button>
              )}

              {/* Produkter accordion with dynamic categories */}
              <MobileAccordion
                label={getContent("nav.products", "Produkter")}
                url={localePath("/catalogue")}
                onNavigate={closeMobile}
                onLabelClick={goToProducts}
              >
                <Link
                  to={localePath("/catalogue")}
                  className="block px-8 py-2 text-gray-900 text-sm font-medium"
                  onClick={closeMobile}
                >
                  <ContentText contentKey="nav.products_all" fallback="Alla produkter" />
                </Link>
                {categoryItems.map((category) => (
                  <Link
                    key={category.slug}
                    to={localePath(`/catalogue/${categoryUrlSlug(category)}`)}
                    className="block px-8 py-2 text-gray-600 text-sm"
                    onClick={closeMobile}
                  >
                    {category.name}
                  </Link>
                ))}
              </MobileAccordion>

              {/* Direct links */}
              {directNavItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.url}
                  className="block px-4 py-3 text-gray-900 text-base font-medium border-b border-gray-200"
                  onClick={closeMobile}
                >
                  {item.label}
                </Link>
              ))}

              <button
                type="button"
                className="flex items-center gap-2 w-full text-left px-4 py-3 text-gray-900 text-base font-medium border-b border-gray-200"
                onClick={() => {
                  closeMobile();
                  setSearchOpen(true);
                }}
              >
                <SearchIcon /> Sök
              </button>

              <div className="border-b border-gray-200">
                <span className="block px-4 py-2 text-gray-500 text-xs uppercase tracking-wider">
                  Språk
                </span>
                {configuredLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    className={`block w-full text-left px-6 py-2 text-base font-medium ${
                      lang.code === locale
                        ? "text-primary bg-primary/10"
                        : "text-gray-700 hover:text-gray-900"
                    }`}
                    onClick={() => {
                      switchToLocale(lang.code);
                      closeMobile();
                    }}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {loginModalOpened && (
        <LoginOrRegisterModal
          onClose={() => setLoginModalOpened(false)}
          onCompleted={(res) => {
            onLogin(res);
            setLoginModalOpened(false);
          }}
        />
      )}

      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </>
  );
};

export default Header;
